import React, { useState, useEffect } from 'react'
import { Table, Button, Modal, Form, Input, Switch, Select, Space, Tag, Alert, Tooltip, DatePicker } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, PlayCircleOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons'
import { apiRequest } from '../../../helpers'
import dayjs from 'dayjs'

const apiUrl = process.env.REACT_APP_API

const GmailAgents = () => {
  const [agents, setAgents] = useState([])
  const [connectors, setConnectors] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [executing, setExecuting] = useState(null)
  const [togglingId, setTogglingId] = useState(null)
  const [selectedId, setSelectedId] = useState(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  useEffect(() => { fetchAll() }, [])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [agentsRes, connRes] = await Promise.all([
        apiRequest(`${apiUrl}agent/gmail`, 'GET'),
        apiRequest(`${apiUrl}connector/gmail`, 'GET'),
      ])
      setAgents(agentsRes?.data?.data || [])
      setConnectors((connRes?.data?.data || []).filter(c => c.connected && c.active))
    } catch { setError('Eroare la incarcare.') }
    finally { setLoading(false) }
  }

  const openCreate = () => {
    setSelected(null)
    setSelectedId(null)
    form.resetFields()
    form.setFieldsValue({ name: 'Gmail Agent', maxEmails: 1, pollInterval: 60, markAsRead: true, autoProcess: false, active: true })
    setModalOpen(true)
  }

  const openEdit = (agent) => {
    setSelected(agent)
    setSelectedId(agent.id)
    form.setFieldsValue({
      name: agent.name,
      gmailConnectorId: agent.gmailConnectorId || undefined,
      startDate: agent.startDate ? dayjs(agent.startDate) : null,
      maxEmails: agent.maxEmails || 1,
      pollInterval: agent.pollInterval || 60,
      markAsRead: agent.markAsRead ?? true,
      autoProcess: agent.autoProcess ?? false,
      active: agent.active ?? true,
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      const payload = {
        ...values,
        gmailConnectorId: values.gmailConnectorId ? Number(values.gmailConnectorId) : null,
        maxEmails: Number(values.maxEmails),
        pollInterval: Number(values.pollInterval) || 60,
        startDate: values.startDate ? values.startDate.toISOString() : null,
      }
      if (selectedId) await apiRequest(`${apiUrl}agent/gmail/${selectedId}`, 'PATCH', payload)
      else await apiRequest(`${apiUrl}agent/gmail`, 'POST', payload)
      setModalOpen(false)
      fetchAll()
    } catch { setError('Eroare la salvare.') }
    finally { setSaving(false) }
  }

  const handleDelete = async () => {
    try {
      await apiRequest(`${apiUrl}agent/gmail/${selected.id}`, 'DELETE')
      setDeleteModalOpen(false)
      fetchAll()
    } catch { setError('Eroare la stergere.') }
  }

  // ── Executie simpla ───────────────────────────────────────

  const handleExecute = async (agent) => {
    setExecuting(agent.id)
    setError(null)
    try {
      const res = await apiRequest(`${apiUrl}agent/gmail/${agent.id}/execute`, 'POST', {})
      const processed = res?.data?.processed || 0
      setSuccess(`Procesat ${processed} emailuri`)
      setTimeout(() => setSuccess(null), 4000)
      fetchAll()
    } catch (e) {
      setError(e?.message || 'Eroare la executie.')
    } finally {
      setExecuting(null)
    }
  }

  // ── Start / Stop auto-procesare ───────────────────────────

  const handleToggleAuto = async (agent) => {
    setTogglingId(agent.id)
    setError(null)
    try {
      if (agent.autoProcess) {
        await apiRequest(`${apiUrl}agent/gmail/${agent.id}/stop`, 'POST', {})
        setSuccess('Auto-procesare oprita.')
      } else {
        await apiRequest(`${apiUrl}agent/gmail/${agent.id}/start`, 'POST', {})
        setSuccess('Auto-procesare pornita.')
      }
      setTimeout(() => setSuccess(null), 3000)
      fetchAll()
    } catch (e) {
      setError(e?.message || 'Eroare.')
    } finally {
      setTogglingId(null)
    }
  }

  // ── Coloane tabel ─────────────────────────────────────────

  const columns = [
    { title: 'Nume', dataIndex: 'name', key: 'name' },
    {
      title: 'Cont Gmail', key: 'gmail',
      render: (_, agent) => {
        const connector = connectors.find(c => c.id === agent.gmailConnectorId)
        return connector?.email || <span style={{ color: '#aaa' }}>—</span>
      }
    },
    {
      title: 'Data start', dataIndex: 'startDate', key: 'startDate',
      render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span>
    },
    {
      title: 'Max emailuri', dataIndex: 'maxEmails', key: 'maxEmails',
      render: (v) => v || 1
    },
    {
      title: 'Interval (s)', dataIndex: 'pollInterval', key: 'pollInterval',
      render: (v) => v || 60
    },
    {
      title: 'Auto', key: 'autoProcess',
      render: (_, agent) => (
        <Tag color={agent.autoProcess ? 'green' : 'default'}>
          {agent.autoProcess ? 'Activ' : 'Oprit'}
        </Tag>
      )
    },
    {
      title: 'Status', dataIndex: 'active', key: 'active',
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Activ' : 'Inactiv'}</Tag>
    },
    {
      title: 'Actiuni', key: 'actions', width: 200,
      render: (_, agent) => (
        <Space>
          {/* Executa o singura data */}
          <Tooltip title="Executa o data">
            <Button
              size="small"
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={executing === agent.id}
              disabled={!!togglingId}
              onClick={() => handleExecute(agent)}
            />
          </Tooltip>

          {/* Start Auto / Stop Auto */}
          <Tooltip title={agent.autoProcess ? 'Opreste auto-procesare' : 'Porneste auto-procesare'}>
            <Button
              size="small"
              type={agent.autoProcess ? 'primary' : 'default'}
              danger={agent.autoProcess}
              icon={agent.autoProcess ? <StopOutlined /> : <SyncOutlined />}
              loading={togglingId === agent.id}
              onClick={() => handleToggleAuto(agent)}
            />
          </Tooltip>

          <Tooltip title="Editeaza">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(agent)}
            />
          </Tooltip>

          <Tooltip title="Sterge">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => { setSelected(agent); setDeleteModalOpen(true) }}
            />
          </Tooltip>
        </Space>
      )
    },
  ]

  // ── Render ────────────────────────────────────────────────

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <strong style={{ fontSize: 16 }}>Agenti Gmail</strong>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Adauga agent</Button>
            <Button icon={<ReloadOutlined />} onClick={fetchAll} />
          </Space>
        </div>

        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} style={{ marginBottom: 12 }} />}
        {success && <Alert message={success} type="success" showIcon style={{ marginBottom: 12 }} />}

        {agents.some(a => a.autoProcess) && (
          <Alert
            message={`Auto-procesare activa pentru: ${agents.filter(a => a.autoProcess).map(a => a.name).join(', ')}`}
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}

        <Alert
          message="Promptul AI si JSON Schema sunt preluate din Agents → Agent Procesare Email"
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
        />

        <Table
          columns={columns}
          dataSource={agents}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: 'Nu exista agenti Gmail.' }}
        />
      </div>

      {/* Modal Create/Edit */}
      <Modal
        title={selected ? 'Editeaza agent Gmail' : 'Adauga agent Gmail'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={saving ? 'Se salveaza...' : 'Salveaza'}
        cancelText="Anuleaza"
        width={600}
      >
        <Alert
          message="Promptul AI, JSON Schema si Conectorul AI sunt preluate din Agent Procesare Email"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <div className="row g-3">
            <div className="col-md-8">
              <Form.Item label="Nume" name="name" rules={[{ required: true, message: 'Numele este obligatoriu' }]}>
                <Input />
              </Form.Item>
            </div>
            <div className="col-md-4" style={{ display: 'flex', alignItems: 'center', paddingTop: 30 }}>
              <Form.Item name="active" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch checkedChildren="Activ" unCheckedChildren="Inactiv" />
              </Form.Item>
            </div>
            <div className="col-md-12">
              <Form.Item label="Cont Gmail" name="gmailConnectorId" rules={[{ required: true, message: 'Selecteaza un cont Gmail' }]}>
                <Select placeholder="-- Selecteaza --">
                  {connectors.map(c => <Select.Option key={c.id} value={c.id}>{c.email || c.name}</Select.Option>)}
                </Select>
              </Form.Item>
              {connectors.length === 0 && <small style={{ color: 'orange' }}>Nu exista conturi Gmail conectate. Adauga din Connectori → Gmail.</small>}
            </div>
            <div className="col-md-6">
              <Form.Item label="Citeste emailuri de la data" name="startDate">
                <DatePicker showTime style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item label="Max emailuri/rulare" name="maxEmails">
                <Input type="number" min={1} />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                label="Interval intre citiri (secunde)"
                name="pollInterval"
                tooltip="Cat timp asteapta agentul intre doua citiri consecutive ale inbox-ului"
              >
                <Input type="number" min={10} addonAfter="sec" />
              </Form.Item>
            </div>
            <div className="col-md-6" style={{ display: 'flex', alignItems: 'center', paddingTop: 8 }}>
              <Form.Item name="markAsRead" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch checkedChildren="Marcheaza ca citit" unCheckedChildren="Nu marca" />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Modal Delete */}
      <Modal
        title="Confirmare stergere"
        open={deleteModalOpen}
        onOk={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Sterge"
        okButtonProps={{ danger: true }}
        cancelText="Anuleaza"
      >
        <p>Stergi agentul <strong>{selected?.name}</strong>?</p>
      </Modal>
    </div>
  )
}

export default GmailAgents
